<template>
    <div class="card mt-4 mission-settings">
        <div class="card-body">
            <h3>Mission</h3>
            <p>The information below is used when generating copy for the various plays made within Avid. For best results, be both concise and descriptive.</p>

            <div class="row">
                <!-- Brief Mission -->
                <div class="col-lg-6 col-12 mb-4">
                    <div class="content-card" :class="{'smart_scanned_card': if_generated.brief_mission, 'is-generating': isGenerating('brief_mission') }" style="border: none; box-shadow: none;">
                        <div class="content-card-header" style="min-height: 72px;">
                            <div class="row w-100 g-2 g-sm-0 align-items-center">
                                <div class="col-sm">
                                    <div class="d-flex align-items-center gap-2">
                                        <h4 class="mb-0">Mission Statement</h4>
                                        <i class="fa fa-info-circle text-muted" 
                                           style="cursor: help; font-size: 16px;"
                                           title="One to Two Sentences"
                                           data-bs-toggle="tooltip"
                                           data-bs-placement="top"></i>
                                    </div>
                                </div>
                                <div class="col-sm-auto d-flex justify-content-end">
                                    <button v-if="!if_generated.brief_mission && !isGenerating('brief_mission') && canEdit('settings')" class="btn btn-primary" @click="regenerateField('brief_mission')">
                                        <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.brief_mission">Refine</span><span v-else>Generate</span>
                                    </button>
                                    <div v-else-if="!if_generated.brief_mission && !isGenerating('brief_mission') && !canEdit('settings')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary" :disabled="true">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.brief_mission">Refine</span><span v-else>Generate</span>
                                        </button>
                                    </div>
                                    <div v-else-if="if_generated.brief_mission && original_values.brief_mission !== undefined" class="d-flex gap-2 justify-content-end">
                                        <button class="btn btn-secondary" @click="revertField('brief_mission')">Revert</button>
                                        <button v-if="canEdit('settings')" class="btn btn-primary" @click="regenerateField('brief_mission')">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> Refine
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="content-card-body">
                            <textarea 
                                ref="briefMissionTextarea"
                                :disabled="!canEdit('settings')"
                                class="form-control mission-textarea"
                                name="brief_mission" 
                                v-model="local_client.brief_mission"
                                @change="autoSave()"
                                @input="autoResize($event)"
                            ></textarea>
                        </div>
                    </div>
                </div>

                <!-- Expanded Mission -->
                <div class="col-lg-6 col-12 mb-4">
                    <div class="content-card" :class="{'smart_scanned_card': if_generated.full_mission, 'is-generating': isGenerating('full_mission') }" style="border: none; box-shadow: none;">
                        <div class="content-card-header" style="min-height: 72px;">
                            <div class="row w-100 g-2 g-sm-0 align-items-center">
                                <div class="col-sm">
                                    <div class="d-flex align-items-center gap-2">
                                        <h4 class="mb-0">Expanded Mission</h4>
                                        <i class="fa fa-info-circle text-muted" 
                                           style="cursor: help; font-size: 16px;"
                                           title="Who do you serve? What do you do?"
                                           data-bs-toggle="tooltip"
                                           data-bs-placement="top"></i>
                                    </div>
                                </div>
                                <div class="col-sm-auto d-flex justify-content-end">
                                    <button v-if="!if_generated.full_mission && !isGenerating('full_mission') && canEdit('settings')" class="btn btn-primary" @click="regenerateField('full_mission')">
                                        <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.full_mission">Refine</span><span v-else>Generate</span>
                                    </button>
                                    <div v-else-if="!if_generated.full_mission && !isGenerating('full_mission') && !canEdit('settings')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary" :disabled="true">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.full_mission">Refine</span><span v-else>Generate</span>
                                        </button>
                                    </div>
                                    <div v-else-if="if_generated.full_mission && original_values.full_mission !== undefined" class="d-flex gap-2 justify-content-end">
                                        <button class="btn btn-secondary" @click="revertField('full_mission')">Revert</button>
                                        <button v-if="canEdit('settings')" class="btn btn-primary" @click="regenerateField('full_mission')">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> Refine
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="content-card-body">
                            <textarea 
                                ref="fullMissionTextarea"
                                :disabled="!canEdit('settings') || isGenerating('full_mission')"
                                class="form-control mission-textarea"
                                name="full_mission" 
                                v-model="local_client.full_mission"
                                @change="autoSave()"
                                @input="autoResize($event)"
                            ></textarea>
                        </div>
                    </div>
                </div>

                <!-- Value Proposition -->
                <div class="col-lg-6 col-12 mb-4">
                    <div class="content-card" :class="{'smart_scanned_card': if_generated.why_give, 'is-generating': isGenerating('why_give') }" style="border: none; box-shadow: none;">
                        <div class="content-card-header" style="min-height: 72px;">
                            <div class="row w-100 g-2 g-sm-0 align-items-center">
                                <div class="col-sm">
                                    <div class="d-flex align-items-center gap-2">
                                        <h4 class="mb-0">Value Proposition</h4>
                                        <i class="fa fa-info-circle text-muted" 
                                           style="cursor: help; font-size: 16px;"
                                           :title="'Why would a donor give to ' + ((local_client.name == null || local_client.name === '') ? 'this organization' : local_client.name) + ' rather than some other nonprofit or not at all?'"
                                           data-bs-toggle="tooltip"
                                           data-bs-placement="top"></i>
                                    </div>
                                </div>
                                <div class="col-sm-auto d-flex justify-content-end">
                                    <button v-if="!if_generated.why_give && !isGenerating('why_give') && canEdit('settings')" class="btn btn-primary" @click="regenerateField('why_give')">
                                        <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.why_give">Refine</span><span v-else>Generate</span>
                                    </button>
                                    <div v-else-if="!if_generated.why_give && !isGenerating('why_give') && !canEdit('settings')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary" :disabled="true">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.why_give">Refine</span><span v-else>Generate</span>
                                        </button>
                                    </div>
                                    <div v-else-if="if_generated.why_give && original_values.why_give !== undefined" class="d-flex gap-2 justify-content-end">
                                        <button class="btn btn-secondary" @click="revertField('why_give')">Revert</button>
                                        <button v-if="canEdit('settings')" class="btn btn-primary" @click="regenerateField('why_give')">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> Refine
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="content-card-body">
                            <textarea 
                                ref="whyGiveTextarea"
                                :disabled="!canEdit('settings') || isGenerating('why_give')"
                                class="form-control mission-textarea"
                                name="why_give" 
                                v-model="local_client.why_give"
                                @change="autoSave()"
                                @input="autoResize($event)"
                            ></textarea>
                        </div>
                    </div>
                </div>

                <!-- Copywriting Instructions -->
                <div class="col-lg-6 col-12 mb-4">
                    <div class="content-card" :class="{'smart_scanned_card': if_generated.style_guide, 'is-generating': isGenerating('style_guide') }" style="border: none; box-shadow: none;">
                        <div class="content-card-header" style="min-height: 72px;">
                            <div class="row w-100 g-2 g-sm-0 align-items-center">
                                <div class="col-sm">
                                    <div class="d-flex align-items-center gap-2">
                                        <h4 class="mb-0">Copywriting Instructions <em>(Optional)</em></h4>
                                        <i class="fa fa-info-circle text-muted" 
                                           style="cursor: help; font-size: 16px;"
                                           title="What are words / phrases that should or should not be used?"
                                           data-bs-toggle="tooltip"
                                           data-bs-placement="top"></i>
                                    </div>
                                </div>
                                <div class="col-sm-auto d-flex justify-content-end">
                                    <button v-if="!if_generated.style_guide && !isGenerating('style_guide') && canEdit('settings')" class="btn btn-primary" @click="regenerateField('style_guide')">
                                        <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.style_guide">Refine</span><span v-else>Generate</span>
                                    </button>
                                    <div v-else-if="!if_generated.style_guide && !isGenerating('style_guide') && !canEdit('settings')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary" :disabled="true">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="local_client.style_guide">Refine</span><span v-else>Generate</span>
                                        </button>
                                    </div>
                                    <div v-else-if="if_generated.style_guide && original_values.style_guide !== undefined" class="d-flex gap-2 justify-content-end">
                                        <button class="btn btn-secondary" @click="revertField('style_guide')">Revert</button>
                                        <button v-if="canEdit('settings')" class="btn btn-primary" @click="regenerateField('style_guide')">
                                            <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> Refine
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="content-card-body">
                            <textarea 
                                ref="styleGuideTextarea"
                                :disabled="!canEdit('settings') || isGenerating('style_guide')"
                                class="form-control mission-textarea"
                                name="style_guide" 
                                v-model="local_client.style_guide"
                                @change="autoSave()"
                                @input="autoResize($event)"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Brand Terms -->
            <div class="mb-3">
                <label class="form-label">Brand Terms <em>(Optional)</em></label>
                <tag-input
                    :disabled="!canEdit('settings')"
                    v-model="local_client.brand_terms"
                    placeholder="Type a term and press Enter or comma to add..."
                    help-text="Add key brand terms, phrases, or terminology that should be used to research news and stories. Each term is searched independently."
                    @change="autoSave()"
                />
            </div>

            <!-- Resource URLs -->
            <div class="mb-3">
                <label class="form-label">Resource URLs <em>(Optional)</em> - Where should we look for stories of impact?</label>
                <div v-for="(url, index) in local_client.resource_urls" :key="index" class="mb-2">
                    <div class="d-flex align-items-center">
                        <input 
                            type="url" 
                            class="form-control flex-grow-1" 
                            :class="{'is-invalid': errors.resource_urls && errors.resource_urls[index]}"
                            :disabled="!canEdit('settings')"
                            v-model="local_client.resource_urls[index]"
                            @change="validateResourceUrl(index); autoSave()"
                            placeholder="https://example.com"
                         aria-label="URL">
                        <button 
                            v-if="canEdit('settings')"
                            class="btn btn-secondary ms-2" 
                            type="button" 
                            @click="removeResourceUrl(index)" data-bs-toggle="tooltip" title="Delete URL"
                        >
                            <img src="/img/icons/avid-icons/trash.svg" alt="Delete URL" class="icon red-icon">
                        </button>
                    </div>
                </div>

                <button 
                    v-if="canEdit('settings')"
                    class="btn btn-sm btn-secondary" 
                    @click="addResourceUrl" data-bs-toggle="tooltip" title="Add URL"
                >
                    <img src="/img/icons/avid-icons/plus.svg" alt="Add URL" class="icon"> Add URL
                </button>
                <div class="text-danger invalid-feedback" v-if="errors.resource_urls && errors.resource_urls.some(error => error)">
                    Please enter valid URLs
                </div>
            </div>

            <!-- Save Button -->
            <div class="mt-4">
                <button class="btn btn-primary px-4" @click="saveChanges">
                    <span v-if="form.busy" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <img class="icon me-1" src="/img/icons/avid-icons/save.svg" v-if="!form.busy" alt=""> Save
                </button>
            </div>
        </div>
    </div>

    <!-- Regenerate Feedback Modal -->
    <div class="modal fade" id="regenerateFeedbackModal" tabindex="-1" aria-labelledby="regenerateFeedbackModalLabel" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <!-- Modal Header -->
                <div class="modal-header pb-0">
                    <h3 class="modal-title fw-bold text-center w-100 mb-0">Refine {{ getFieldTitle(regenerate_feedback_data.field_key) }}</h3>
                    <button type="button" class="btn btn-white float-end close-modal" @click="closeRegenerateFeedbackModal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body px-4 pb-4">
                    <label for="regenerate_feedback" class="form-label">Describe the changes you'd like:</label>
                    <textarea 
                        id="regenerate_feedback"
                        v-model="regenerate_feedback_data.feedback_text"
                        class="form-control" 
                        rows="4"
                        style="min-height: 100px;"
                        :class="{ 'is-invalid': regenerate_feedback_data.error_message }"
                    ></textarea>
                    <div v-if="regenerate_feedback_data.error_message" class="invalid-feedback">
                        {{ regenerate_feedback_data.error_message }}
                    </div>
                </div>

                <!-- Modal Footer -->
                <div class="modal-footer">
                    <div class="w-100 d-flex justify-content-end">
                        <button 
                            type="button" 
                            class="btn btn-secondary me-2"
                            @click="closeRegenerateFeedbackModal"
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-primary"
                            :disabled="!regenerate_feedback_data.feedback_text.trim()"
                            @click="submitRegenerateFeedback"
                        >
                            Refine
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../../mixins/permissions';
import Swal from 'sweetalert2';
import { useNotifications } from '../../../composables/useNotifications';
import TagInput from '../../components/TagInput.vue';

export default defineComponent({
    name: 'MissionSettings',
    components: {
        TagInput
    },
    mixins: [permissionsMixin],
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            local_client: {},
            form: {
                busy: false,
                error: false,
                dirty: false
            },
            errors: {
                resource_urls: []
            },
            // Regeneration state
            if_generated: {}, // Track which fields have been regenerated
            generating_fields: [], // Track which fields are currently generating
            original_values: {}, // Store original content before regeneration
            regenerate_feedback_data: {
                field_key: '',
                feedback_text: '',
                error_message: ''
            }
        };
    },
    beforeMount() {
        this.local_client = { ...this.client };
        
        // Clean up brand_terms: trim trailing commas and whitespace
        if (this.local_client.brand_terms) {
            this.local_client.brand_terms = this.local_client.brand_terms.replace(/,\s*$/, '').trim();
        }
        
        // Initialize resource_urls as array if not set, start with one empty field
        if (!this.local_client.resource_urls || this.local_client.resource_urls.length === 0) {
            this.local_client.resource_urls = [''];
        }
        
        // Initialize errors.resource_urls array
        this.errors.resource_urls = new Array(this.local_client.resource_urls.length).fill('');
    },
    mounted() {
        // Auto-resize all textareas on initial load
        this.$nextTick(() => {
            this.autoResizeAllTextareas();
        });
        
        // Listen for brand info regeneration events via Pusher
        if (this.client && this.client.id) {
            window.Echo.private(`client.${this.client.id}`)
                .listen('BrandInfoRegenerated', (e) => {
                    // Only update the specific field that was returned
                    if (this.generating_fields.includes(e.field)) {
                        this.local_client[e.field] = e.value;
                        this.if_generated[e.field] = true;
                        
                        // Remove from generating state
                        const index = this.generating_fields.indexOf(e.field);
                        if (index > -1) {
                            this.generating_fields.splice(index, 1);
                        }
                        
                        // Auto-resize textarea and mark dirty
                        this.$nextTick(() => {
                            this.autoResizeAllTextareas();
                        });
                        this.form.dirty = true;
                        
                        // Show success message
                        const fieldTitle = this.getFieldTitle(e.field);
                        const hasOriginalContent = this.original_values[e.field] && this.original_values[e.field].trim().length > 0;
                        const successMessage = hasOriginalContent
                            ? `${fieldTitle} refined successfully!`
                            : `${fieldTitle} generated successfully!`;
                        
                        this.showNotification({
                            text: successMessage
                        });
                    }
                });
        }
    },
    
    beforeUnmount() {
        // Clean up Pusher listener
        if (this.client && this.client.id) {
            window.Echo.private(`client.${this.client.id}`).stopListening('BrandInfoRegenerated');
        }
    },
    methods: {
        autoResize(event) {
            const textarea = event.target;
            // Reset height to auto to get accurate scrollHeight
            textarea.style.height = 'auto';
            // Get the scrollHeight (content height)
            const scrollHeight = textarea.scrollHeight;
            // Set height to scrollHeight, but ensure minimum height of 4 rows (approximately 100px)
            const minHeight = 100; // Minimum 100px (about 4 rows)
            const newHeight = Math.max(scrollHeight, minHeight);
            textarea.style.height = newHeight + 'px';
            // Hide scrollbar if content fits within the textarea (with small buffer for rounding)
            // Only show scrollbar if content actually overflows
            textarea.style.overflowY = scrollHeight > newHeight + 2 ? 'auto' : 'hidden';
        },
        autoResizeAllTextareas() {
            // Get all textarea refs and resize them
            const textareaRefs = [
                'briefMissionTextarea',
                'fullMissionTextarea',
                'whyGiveTextarea',
                'styleGuideTextarea'
            ];
            
            textareaRefs.forEach(refName => {
                const textarea = this.$refs[refName];
                if (textarea) {
                    // Reset height to auto to get accurate scrollHeight
                    textarea.style.height = 'auto';
                    // Get the scrollHeight (content height)
                    const scrollHeight = textarea.scrollHeight;
                    // Set height to scrollHeight, but ensure minimum height of 4 rows (approximately 100px)
                    const minHeight = 100; // Minimum 100px (about 4 rows)
                    const newHeight = Math.max(scrollHeight, minHeight);
                    textarea.style.height = newHeight + 'px';
                    // Hide scrollbar if content fits within the textarea (with small buffer for rounding)
                    textarea.style.overflowY = scrollHeight > newHeight + 2 ? 'auto' : 'hidden';
                }
            });
        },
        hasError() {
            return this.errors.resource_urls && this.errors.resource_urls.some(error => error !== "");
        },
        getClientDataForSave() {
            // Create a copy of the client data and filter out blank resource URLs
            const clientData = { ...this.local_client };
            clientData.resource_urls = this.local_client.resource_urls.filter(url => url && url.trim() !== '');
            return clientData;
        },
        autoSave() {
            // mark form as dirty when changes occur and validation passes
            if (!this.canEdit('settings') || this.hasError()) {
                return;
            }
            this.form.dirty = true;
        },
        saveChanges() {
            if (!this.canEdit('settings') || this.hasError()) return;
            this.form.busy = true;
            window.axios.put('/api/client/' + this.local_client.url, this.getClientDataForSave())
                .then(() => {
                    this.form.busy = false;
                    this.form.successful = true;
                    this.form.dirty = false;
                    
                    // Reset generation state after successful save
                    this.if_generated = {};
                    this.original_values = {};
                    
                    this.showNotification({
                        text: 'Your settings were successfully saved!'
                    });
                }).catch(() => {
                    this.form.busy = false;
                    this.showNotification({
                        text: 'Failed to save your settings. Please try again.',
                        type: 'error'
                    });
                });
        },
        
        // Regeneration methods
        getFieldTitle(fieldKey) {
            const titles = {
                'brief_mission': 'Mission Statement',
                'full_mission': 'Expanded Mission',
                'why_give': 'Value Proposition',
                'style_guide': 'Copywriting Instructions'
            };
            return titles[fieldKey] || 'Content';
        },
        
        regenerateField(fieldKey) {
            // Check if this field is currently generating
            if (this.isGenerating(fieldKey)) {
                return;
            }
            
            // Check if this is a regenerate (existing content) or generate (new content)
            const hasExistingContent = this.local_client[fieldKey] && this.local_client[fieldKey].trim().length > 0;
            
            if (hasExistingContent) {
                // Show feedback modal for regeneration
                this.showRegenerateFeedbackModal(fieldKey);
            } else {
                // Direct generation for new content
                this.startFieldGeneration(fieldKey);
            }
        },
        
        showRegenerateFeedbackModal(fieldKey) {
            this.regenerate_feedback_data.field_key = fieldKey;
            this.regenerate_feedback_data.feedback_text = '';
            this.regenerate_feedback_data.error_message = '';
            
            const modalElement = document.getElementById('regenerateFeedbackModal');
            const modal = new bootstrap.Modal(modalElement);
            modal.show();
        },
        
        closeRegenerateFeedbackModal() {
            const modalElement = document.getElementById('regenerateFeedbackModal');
            const modal = bootstrap.Modal.getInstance(modalElement);
            if (modal) {
                modal.hide();
            }
            this.resetRegenerateFeedbackForm();
        },
        
        resetRegenerateFeedbackForm() {
            this.regenerate_feedback_data.field_key = '';
            this.regenerate_feedback_data.feedback_text = '';
            this.regenerate_feedback_data.error_message = '';
        },
        
        async submitRegenerateFeedback() {
            // Validate feedback text
            if (!this.regenerate_feedback_data.feedback_text.trim()) {
                this.regenerate_feedback_data.error_message = 'Please describe how you would like the strategy to be changed.';
                return;
            }
            
            // Clear any previous error
            this.regenerate_feedback_data.error_message = '';
            
            // Store field key and feedback before closing modal
            const fieldKey = this.regenerate_feedback_data.field_key;
            const feedbackText = this.regenerate_feedback_data.feedback_text;
            
            // Add field to generating state BEFORE closing modal so Vue can update immediately
            if (!this.generating_fields.includes(fieldKey)) {
                this.generating_fields.push(fieldKey);
            }
            
            // Store original content if not already stored
            if (!this.original_values[fieldKey]) {
                this.original_values[fieldKey] = this.local_client[fieldKey] || '';
            }
            
            // Wait for Vue to update the DOM
            await this.$nextTick();
            
            // Close the modal
            this.closeRegenerateFeedbackModal();
            
            // Start generation with feedback (this will handle the rest of the generation process)
            this.startFieldGeneration(fieldKey, feedbackText);
        },
        
        async startFieldGeneration(fieldKey, feedbackText = '') {
            // Add this field to generating state
            if (!this.generating_fields.includes(fieldKey)) {
                this.generating_fields.push(fieldKey);
            }
            
            // Store original content if not already stored
            if (!this.original_values[fieldKey]) {
                this.original_values[fieldKey] = this.local_client[fieldKey] || '';
            }
            
            // Wait for Vue to update the DOM
            await this.$nextTick();
            
            try {
                // Call the API endpoint
                const payload = {
                    field: fieldKey,
                    current_content: this.local_client[fieldKey] || '',
                    feedback: feedbackText || null
                };
                
                await window.axios.post(`/api/client/${this.client.url}/regenerate-brand-info`, payload);
                
                // The actual update will happen via Pusher event
                // We don't show success here since it will be handled by the Pusher listener
                
            } catch (error) {
                console.error('Error generating content:', error);
                
                // Remove from generating state on error
                const index = this.generating_fields.indexOf(fieldKey);
                if (index > -1) {
                    this.generating_fields.splice(index, 1);
                }
                
                // Show error message
                const fieldTitle = this.getFieldTitle(fieldKey);
                const errorMessage = feedbackText.trim().length > 0
                    ? `Failed to refine the content for ${fieldTitle}. Please try again.`
                    : `Failed to generate ${fieldTitle}. Please try again.`;
                
                this.showNotification({
                    text: errorMessage,
                    type: 'error'
                });
            }
        },
        
        isGenerating(fieldKey = null) {
            if (fieldKey) {
                return this.generating_fields.includes(fieldKey);
            }
            return this.generating_fields.length > 0;
        },
        
        revertField(fieldKey) {
            if (this.original_values[fieldKey] !== undefined) {
                // Swap new content with original content
                this.local_client[fieldKey] = this.original_values[fieldKey];
                this.original_values[fieldKey] = null;
                
                // Remove from generated state
                delete this.if_generated[fieldKey];
                
                // Auto-resize textarea
                this.$nextTick(() => {
                    this.autoResizeAllTextareas();
                });
                
                // Mark form as dirty
                this.form.dirty = true;
            }
        },
        
        // Resource URLs methods
        addResourceUrl() {
            this.local_client.resource_urls.push('');
            this.errors.resource_urls.push('');
            this.form.dirty = true;
        },
        
        removeResourceUrl(index) {
            const urlToRemove = this.local_client.resource_urls[index];
            
            // Prompt user about deleting related resources with checkbox
            Swal.fire({
                title: 'Remove URL',
                html: `
                    <div>
                        <p>Are you sure you want to remove this URL?</p>
                        <p><strong>${urlToRemove}</strong></p>
                        <div class="form-check mt-2 mx-auto d-flex flex-column align-items-center" style="max-width: 320px; text-align: left;">
                            <div class="w-100 text-start">
                                <input class="form-check-input" type="checkbox" id="deleteResources" checked aria-label="Checkbox">
                                <label class="form-check-label" for="deleteResources">
                                    Also remove related resources (stories, news, etc.) pulled from this URL
                                </label>
                            </div>
                        </div>
                    </div>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Remove URL',
                cancelButtonText: 'Cancel',
                customClass: {
                    popup: 'swal-warning'
                },
                preConfirm: () => {
                    const deleteResources = document.getElementById('deleteResources').checked;
                    return { deleteResources };
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    const deleteResources = result.value.deleteResources;
                    if (deleteResources) {
                        // Delete URL and related resources
                        this.deleteResourceUrlWithCleanup(index, urlToRemove);
                    } else {
                        // Delete URL only
                        this.deleteResourceUrlOnly(index);
                    }
                }
            });
        },
        
        deleteResourceUrlWithCleanup(index, url) {
            // Remove from local array first
            this.local_client.resource_urls.splice(index, 1);
            this.errors.resource_urls.splice(index, 1);
            
            // Call API to delete URL and clean up related vectors
            window.axios.delete(`/api/client/${this.client.url}/resource-url`, {
                data: {
                    url: url,
                    delete_resources: true
                }
            }).then(response => {
                this.autoSave();
                this.showNotification({
                    text: 'The URL and all related resources were removed!'
                });
            }).catch(error => {
                // Restore the URL if API call failed
                this.local_client.resource_urls.splice(index, 0, url);
                this.errors.resource_urls.splice(index, 0, '');
                
                this.showNotification({
                    text: 'Failed to delete resources. Please try again.',
                    type: 'error'
                });
            });
        },
        
        deleteResourceUrlOnly(index) {
            this.local_client.resource_urls.splice(index, 1);
            this.errors.resource_urls.splice(index, 1);
            this.form.dirty = true;
        },
        
        validateResourceUrl(index) {
            const url = this.local_client.resource_urls[index];
            if (url && url.trim() !== '') {
                try {
                    new URL(url);
                    this.errors.resource_urls[index] = '';
                } catch {
                    this.errors.resource_urls[index] = 'Please enter a valid URL';
                }
            } else {
                this.errors.resource_urls[index] = '';
            }
        }
    }
});
</script>

